import React, { useEffect, useState } from "react";
import { PageHeader } from "components/ui";
import { Grid, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import GriFormComponent from "components/customizedStandardsComponent/GriFormComponent";
import FreeTextInputComponent from "components/customizedStandardsComponent/FreeTextInputComponent";
import UploadEvidenceComponent from "components/customizedStandardsComponent/UploadEvidenceComponent";
import DownloadReport from "components/customizedStandardsComponent/DownloadReport";
import DownloadPostReviewGapForm from "components/customizedStandardsComponent/DownloadPostReviewGapForm";
import UploadPostReviewGapForm from "components/customizedStandardsComponent/UploadPostReviewGapForm";
import DownloadPostReviewReports from "components/customizedStandardsComponent/DownloadPostReviewReports";
import StandardsSelector from "components/customizedStandardsComponent/GriStandardsSelector";
import NavigationButtons from "helper/navigation/NavigationButtons";
import ReusableFormComponent from "components/customizedStandardsComponent/EsgFormComponent";
import UploadEvidenceReviewReports from "components/customizedStandardsComponent/UploadEvidenceReviewReports";
import ReportsReviewDialog from "components/customizedStandardsComponent/ReportsReviewDialog";

import {
  CLIMATE_IMPACT_ASSESSMENT_FORM,
  CLIMATE_IMPACT_SMART_DATA_FORM,
  ENVIROMENTAL_COMPLIANCE_REPORTING_FORM,
  BIODIVERSITY_ASSESSTMENT_REPORTING_FORM,
  WATER_WASTE_RESOURCE_FORM,
  CLIMATE_RISK_REPORTING_FORM,
  EMISSION_POLLUTION_CONTROL_FORM,
} from "data/forms/griFormSchema";

import {
  CLIMATE_IMPACT_STANDARDS,
  ENVIROMENTAL_COMPLIANCE_REPORTING,
} from "data/standards/griStandards";

import { FREE_TEXT_ENVIROMENT } from "data/prompts/freeTextPrompts";

import {
  generatedReportsAssClimateImpact,
  frameWorkandStandardMapping,
  legalAndFrameWorkMapping,
  reportsAssesstmentOption,
} from "data/reports/relevantReports";

const EnviromentalComplianceStepper = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [prevStep, setPrevStep] = useState(0);
  const [reportData, setReportData] = useState({
    selectedStandards: [],
    companyInfo: {},
  });
  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);

  const steps = [
    "Select GRI Standards",
    "Free Text Input & Prompts",
    "Download Emissions Setup Guide",
    "Biodiversity Assessment Reporting",
    "Water, Waste & Resource Form",
    "Climate Risk Reporting",
    "Emission & Pollution Control",
    "Download Climate Impact Reports",
    "Upload Evidence and Review Reports",
    "Download Post-Review Reports",
    "Download Gap Form",
    "Upload Gap Form",
    "Post-Review Report Summary",
  ];

  const handleNext = () => {
    setPrevStep(activeStep);
    setActiveStep((prev) => prev + 1);
  };

  useEffect(() => {
    if (activeStep === 9 && prevStep === 8) {
      setShowReviewPopup(true);
    }
    setForceUpdate((prev) => prev + 1);
  }, [activeStep, prevStep]);

  const handleBack = () => {
    setPrevStep(activeStep);
    setActiveStep((prev) => prev - 1);
  };

  const handleSaveStandards = (selectedStandards) => {
    setReportData((prev) => ({
      ...prev,
      selectedStandards,
    }));
  };

  const handleClosePopup = () => {
    setShowReviewPopup(false);
  };

  const handleSkipToDashboard = () => {
    setShowReviewPopup(false);
    navigate("/"); // Navigate to dashboard
  };

  const DEFAULT_EVIDENCE_TYPES = [
    "Baseline Environmental Surveys (air, water, soil, biodiversity)",
    "Monitoring Logs and Sampling Results",
    "Satellite Imagery / GIS Layers (KMZ, GeoJSON, PNG)",
    "Emission Lab Results",
    "Permits, Licenses, EIA NOCs",
    "Spill or Incident Logs",
    "NGO or Regulator Comments",
    "Third-party EIA Reports",
    "Ambient Noise/Water Quality Measurement Reports",
    "Waste Manifest and Disposal Records",
    "Water Balance Sheets",
    "Others as required",
  ];

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StandardsSelector
            standards={ENVIROMENTAL_COMPLIANCE_REPORTING}
            onNext={handleNext}
            onSave={handleSaveStandards}
            title="Report Type Selection – What Do You Want to Generate?"
            nextButtonText="Next"
            initialSelectedStandards={reportData.selectedStandards.map(
              (s) => s.id
            )}
          />
        );

      case 1:
        return (
          <FreeTextInputComponent
            stepData={FREE_TEXT_ENVIROMENT}
            onNext={handleNext}
            showSaveButton={true}
            showNextButton={true}
            titleColor="#02462E"
            subtitleColor="#02462E"
            formWidth={707}
          />
        );
      case 2:
        return (
          <DownloadReport
            key="emissions-setup-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={legalAndFrameWorkMapping}
            title="Legal and Framework Mapping"
            description="Download and review emissions tracking guidelines and frameworks"
            stepId="step-3"
            ReportDownloadButtonShow={false}
          />
        );
      case 3:
        return (
          <ReusableFormComponent
            {...BIODIVERSITY_ASSESSTMENT_REPORTING_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 4:
        return (
          <ReusableFormComponent
            {...WATER_WASTE_RESOURCE_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 5:
        return (
          <ReusableFormComponent
            {...CLIMATE_RISK_REPORTING_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
          />
        );
      case 6:
        return (
          <ReusableFormComponent
            {...EMISSION_POLLUTION_CONTROL_FORM}
            onNext={handleNext}
            onPrevious={handleBack}
            showPreviousButton={true}
            title="Emissions and Pollution Control Form"
          />
        );
      case 7:
        return (
          <UploadEvidenceComponent
            onPrevious={handleBack}
            onNext={handleNext}
            evidenceTypes={DEFAULT_EVIDENCE_TYPES}
          />
        );

      case 8:
        return (
          <DownloadReport
            key="climate-impact-reports-step"
            onPrevious={handleBack}
            onNext={handleNext}
            initialItems={reportsAssesstmentOption}
            showReportCountdownButton={true}
            title="Download Report relevant to your requirement"
            description="Download and review generated climate impact reports"
            stepId="step-8"
          />
        );
      case 9:
        return (
          <UploadEvidenceReviewReports
            onPrevious={handleBack}
            onNext={handleNext}
            
          />
        );
      case 10:
        return (
          <DownloadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 11:
        return (
          <UploadPostReviewGapForm
            onPrevious={handleBack}
            onNext={handleNext}
          />
        );
      case 12:
        return (
          <DownloadPostReviewReports
            onPrevious={handleBack}
            standardName="Enviromental Complicance"
            showReportCountdownButton={true}
          />
        );
      default:
        return <div>Unknown step 🥲</div>;
    }
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeader title="AI-Powered ESG Reporting across Global, National, Sectoral, and Industry-Specific Standards" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <NavigationButtons
              imageKey="dashboardImg3_2"
              title="Environmental Compliance"
              activeStep={activeStep}
              onBack={handleBack}
            />
          </Box>
        </Grid>

        <Grid item xs={12}>
          {getStepContent(activeStep)}
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
              mb: 2,
            }}
          >
            {steps.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: index <= activeStep ? "#02462E" : "#e0e0e0",
                  mx: 1,
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <ReportsReviewDialog
        open={showReviewPopup}
        onClose={handleClosePopup}
        onSkip={handleSkipToDashboard}
      />
    </>
  );
};

export default EnviromentalComplianceStepper;
